// Full-document render of the tempurpedic ProductLinePage: PageSkeleton (the
// React document shell) wrapping the content -- the hero (OverlayBanner +
// PDPHero + configurator, or the alternative-design configurator_based_modules
// render + sticky configurator) and the consolidated PLP body -- with the same
// container/section DOM the product-detail / product-line SCSS anchors to
// (#product-detail-container, #product-detail, .pdp-hero, .pdp-hero--relative,
// .pdp-capped-width-content, .pdp-hero__content, .pdp-sticky-configurator).
// Unlike the PDP, the body is NOT wrapped in .pdp-blocks -- it is a direct
// child of #product-detail.
import React from "react";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "tsi-common-react/src/apps/configurator/constants";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { PageSkeleton } from "../components/PageSkeleton";
import { ProductLinePageBody } from "../components/ProductLinePageBody";
import { ProductPageBody } from "../components/productModulesBody";
import { Template as ModularConfigurator } from "./ModularConfiguratorTemplate";
import { Template as OverlayBannerAssembly } from "./OverlayBannerAssemblyTemplate";
import { Template as PDPHero } from "./PDPHeroTemplate";
import { Template as StickyConfigurator } from "./StickyConfiguratorTemplate";

/**
 * PDP sticky-rail behavior. On the alternative-design PLP the configurator
 * column (`.pdp-capped-width-content .pdp-hero__content`) sticks to the viewport
 * as the `.pdp-capped-width-content` rail scrolls, then pins (`.static`) at the
 * rail's end; on mobile the top sticky configurator
 * (`.pdp-sticky-configurator .sticky-configurator`) hides near the footer. The
 * targets span the page body AND the site chrome, so they are queried from the
 * document in a client-only effect that tears down its scroll/resize listeners.
 * Every branch is guarded on the queried elements, so where the configurator
 * selector does not match (the default-design PDP, or the PDP display page) the
 * effect is an inert no-op.
 */
const usePdpStickyRail = (): void => {
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (!win || !doc) {
            return;
        }
        const isMobileWidth = () =>
            win.innerWidth < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE;

        const topStickyConfiguratorContainer = doc.querySelector<HTMLElement>(
            ".pdp-sticky-configurator",
        );

        const hideTopStickyConfigurator = (
            hide: boolean,
            element: HTMLElement | null = null,
        ): void => {
            if (!isMobileWidth() || !topStickyConfiguratorContainer) {
                return;
            }
            const target = element ?? topStickyConfiguratorContainer;
            if (hide) {
                target.classList.add("hide");
            } else {
                target.classList.remove("hide");
            }
        };

        let largeScreenTarget = 0;
        const searchNav = doc.querySelector<HTMLElement>(".search-nav");
        const siteHeader = doc.querySelector<HTMLElement>(".site-header");
        const configurator = doc.querySelector<HTMLElement>(
            ".pdp-capped-width-content .pdp-hero__content",
        );
        const rail = doc.querySelector<HTMLElement>(
            ".pdp-capped-width-content",
        );
        const footer = doc.querySelector<HTMLElement>(".site-footer");
        for (const elem of [searchNav, siteHeader]) {
            largeScreenTarget += elem ? elem.offsetHeight : 0;
        }

        const onScroll = (): void => {
            if (!isMobileWidth()) {
                if (!configurator) {
                    return;
                }
                if (win.scrollY >= largeScreenTarget) {
                    configurator.classList.add("sticky");
                    if (rail) {
                        const threshold =
                            rail.offsetHeight +
                            largeScreenTarget -
                            configurator.scrollHeight;
                        const yPosition =
                            rail.offsetHeight - configurator.scrollHeight;
                        if (
                            win.scrollY >= threshold &&
                            yPosition >= largeScreenTarget
                        ) {
                            configurator.classList.remove("sticky");
                            configurator.classList.add("static");
                            configurator.style.top = `${yPosition}px`;
                        } else {
                            configurator.classList.add("sticky");
                            configurator.classList.remove("static");
                            configurator.style.top = "0";
                        }
                    }
                } else {
                    configurator.classList.remove("sticky");
                }
                return;
            }
            if (configurator) {
                configurator.classList.remove("sticky");
            }
            if (!topStickyConfiguratorContainer) {
                return;
            }
            const topStickyConfigurator =
                topStickyConfiguratorContainer.querySelector<HTMLElement>(
                    ".sticky-configurator",
                );
            if (footer && topStickyConfigurator) {
                const footerOffsetTop =
                    footer.getBoundingClientRect().top -
                    doc.body.getBoundingClientRect().top -
                    topStickyConfigurator.offsetHeight;
                hideTopStickyConfigurator(
                    win.scrollY >= footerOffsetTop,
                    topStickyConfigurator,
                );
            }
        };

        const onResize = (): void => {
            if (isMobileWidth() && configurator) {
                configurator.classList.remove("sticky");
            }
        };

        win.addEventListener("scroll", onScroll);
        win.addEventListener("resize", onResize);
        return () => {
            win.removeEventListener("scroll", onScroll);
            win.removeEventListener("resize", onResize);
        };
    }, []);
};

// Mutable reactivated props (not Immutable<>): PageSkeleton composes the in-tree
// SiteHeader chain, which is typed with mutable reactivated props. The hero /
// body templates accept Immutable props, to which these are assignable.
export const Template = (
    props: templates.ProductLinePageTemplate,
): React.JSX.Element => {
    const chrome = React.useContext(Context).chrome;
    usePdpStickyRail();
    // Alternative-design hero: the whole configurator_based_modules StreamField,
    // rendered through the same page-body assembly as `modules` (`wrapBlocks`
    // adds the per-block `w-block-{type}` wrappers this whole-field render
    // needs), fed its own id-keyed side-channels.
    const cbmBlocks = props.page.configurator_based_modules?.value ?? [];
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div id="product-detail-container">
                <div id="product-detail">
                    {props.use_default_design ? (
                        <div
                            className={`pdp-hero${
                                props.overlay_banner_relative
                                    ? " pdp-hero--relative"
                                    : ""
                            }`}
                        >
                            <OverlayBannerAssembly {...props.overlay_banner} />
                            <PDPHero {...props.hero} />
                            {/* The configurator column always renders; the
                                configurator itself is gated on the FK. */}
                            <div className="pdp-hero__content l-capped-width">
                                {props.configurator && (
                                    <ModularConfigurator
                                        {...props.configurator}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="pdp-hero pdp-capped-width-content">
                            <div
                                className={
                                    props.has_configurator
                                        ? undefined
                                        : "no-configurator"
                                }
                            >
                                <ProductPageBody
                                    value={cbmBlocks}
                                    wrapBlocks={true}
                                    hardcoded_ssr={props.cbm_hardcoded_ssr}
                                    hotspot_ssr={props.cbm_hotspot_ssr}
                                    breadcrumbs_ssr={props.cbm_breadcrumbs_ssr}
                                    customer_service_ssr={
                                        props.cbm_customer_service_ssr
                                    }
                                    shop_compare_ssr={
                                        props.cbm_shop_compare_ssr
                                    }
                                    tab_feature_attribute_ssr={
                                        props.cbm_tab_feature_attribute_ssr
                                    }
                                    reviews_ssr={props.cbm_reviews_ssr}
                                    reviews={props.cbm_reviews}
                                    pageNumReviews={props.page_num_reviews}
                                    changeVariantSSR={
                                        props.cbm_change_variant_ssr
                                    }
                                    changeRootSSR={props.cbm_change_root_ssr}
                                    rootConditionalSSR={
                                        props.cbm_root_conditional_ssr
                                    }
                                    variantConditionalSSR={
                                        props.cbm_variant_conditional_ssr
                                    }
                                    tabCardsInitialRootProductID={
                                        props.cbm_tab_cards_initial_root_product_id
                                    }
                                />
                                {props.has_configurator && (
                                    <>
                                        <div className="pdp-hero__content l-capped-width">
                                            {props.configurator && (
                                                <ModularConfigurator
                                                    {...props.configurator}
                                                />
                                            )}
                                        </div>
                                        {/* This wrapper div is the
                                            `configurator-sticky.ts` observed
                                            hook. */}
                                        {props.configurator &&
                                            props.sticky_configurator && (
                                                <div className="pdp-sticky-configurator">
                                                    <StickyConfigurator
                                                        {...props.configurator}
                                                        {...props.sticky_configurator}
                                                        is_plc_version={false}
                                                        category_selectors_data="[]"
                                                        sticky_trigger_selector=".pdp-hero__content"
                                                        edit_scroll_target_selector=".pdp-capped-width-content .pdp-hero__content"
                                                    />
                                                </div>
                                            )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    <ProductLinePageBody
                        page={props.page}
                        hardcoded_ssr={props.hardcoded_ssr}
                        hotspot_ssr={props.hotspot_ssr}
                        breadcrumbs_ssr={props.breadcrumbs_ssr}
                        customer_service_ssr={props.customer_service_ssr}
                        shop_compare_ssr={props.shop_compare_ssr}
                        tab_feature_attribute_ssr={
                            props.tab_feature_attribute_ssr
                        }
                        reviews_ssr={props.reviews_ssr}
                        reviews={props.reviews}
                        page_num_reviews={props.page_num_reviews}
                        change_variant_ssr={props.change_variant_ssr}
                        change_root_ssr={props.change_root_ssr}
                        root_conditional_ssr={props.root_conditional_ssr}
                        variant_conditional_ssr={props.variant_conditional_ssr}
                        tab_cards_initial_root_product_id={
                            props.tab_cards_initial_root_product_id
                        }
                    />
                </div>
            </div>
            <IovationScripts
                iovationUrl={props.iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
